import { Check, Tag, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal } from "@/components/site/Reveal";
import { ATT_BLUE, ATT_CATALOG, ATT_PLAN_DETAILS, ATT_INTERNET, ATT_PROMOS } from "@/attData";

export const SubpageBanner = ({ src, alt, testid, contain = false }) => (
  <div className={`overflow-hidden rounded-3xl border border-black/10 shadow-sm ${contain ? "bg-[#d5d5d5]" : ""}`}>
    <img src={src} alt={alt} className={`w-full h-40 md:h-60 ${contain ? "object-contain" : "object-cover"}`} data-testid={testid} loading="lazy" />
  </div>
);

export const SectionLabel = ({ children }) => (
  <div className="flex items-center gap-3">
    <span className="h-[3px] w-10" style={{ backgroundColor: ATT_BLUE }} />
    <p className="font-body text-xs md:text-sm font-bold tracking-[0.25em] uppercase" style={{ color: ATT_BLUE }}>{children}</p>
  </div>
);

export const AudienceToggle = ({ value, onChange }) => (
  <div className="inline-flex rounded-full border border-black/15 bg-white p-1 shadow-sm" data-testid="att-audience-toggle">
    {[["residential", "Residential"], ["business", "Business"]].map(([v, label]) => (
      <button
        key={v}
        onClick={() => onChange(v)}
        data-testid={`att-toggle-${v}`}
        className={`rounded-full px-6 py-2.5 font-body text-sm font-semibold transition-colors duration-300 ${value === v ? "text-white" : "text-[#3c4650] hover:text-[#00A8E0]"}`}
        style={value === v ? { backgroundColor: ATT_BLUE } : {}}
      >
        {label}
      </button>
    ))}
  </div>
);

export const DeviceCard = ({ d }) => (
  <Link to={`/att/devices/${d.id}`} data-testid={`att-device-${d.id}`} className="group h-full rounded-2xl border border-black/10 bg-white p-5 shadow-sm flex flex-col transition-all duration-300 hover:shadow-lg hover:border-[#00A8E0]/50 hover:-translate-y-0.5">
    <div className="h-44 flex items-center justify-center bg-[#f7f9fa] rounded-xl">
      <img src={d.img} alt={d.name} className="max-h-40 max-w-full w-auto object-contain" loading="lazy" />
    </div>
    <p className="mt-4 font-body text-[11px] uppercase tracking-widest text-[#6b7683]">{d.brand}</p>
    <h4 className="mt-0.5 font-serif-display text-lg font-bold text-[#1d2329] leading-snug">{d.name}</h4>
    <p className="mt-1.5 font-body text-sm text-[#3c4650]">
      {d.monthly ? (
        <><span className="font-bold text-[#1d2329] text-base">{d.monthly}</span> for 36 mo. · <span className="text-[#6b7683]">{d.retail} retail</span></>
      ) : (
        <span className="font-bold text-[#1d2329] text-base">{d.retail}</span>
      )}
    </p>
    <p className="mt-1 font-body text-xs font-semibold" style={{ color: ATT_BLUE }}>{d.promo}</p>
    {d.noTradeIn && (
      <span className="mt-2 inline-flex w-fit items-center rounded-full bg-emerald-100 px-2.5 py-0.5 font-body text-[10px] font-bold uppercase tracking-wider text-emerald-700" data-testid={`att-notradein-${d.id}`}>
        No trade-in needed
      </span>
    )}
    {d.storage && (
      <p className="mt-2 font-body text-[11px] text-[#6b7683]"><span className="font-semibold text-[#3c4650]">{d.storage.some((s) => s.includes("GB") || s.includes("TB")) ? "Storage" : "Size"}:</span> {d.storage.join(" · ")}</p>
    )}
    <div className="mt-auto pt-4">
      <div className="flex items-center gap-1.5 flex-wrap">
        {d.colors.map(([n, h]) => (
          <span key={n} title={n} className="h-4 w-4 rounded-full border border-black/20" style={{ backgroundColor: h }} />
        ))}
      </div>
      <p className="mt-1.5 font-body text-[11px] leading-relaxed text-[#6b7683]">{d.colors.map(([n]) => n).join(" · ")}</p>
      <span className="mt-3 inline-flex items-center gap-1.5 font-body text-xs font-bold transition-colors" style={{ color: ATT_BLUE }} data-testid={`att-device-customize-${d.id}`}>
        Customize &amp; Pre-Order <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
      </span>
    </div>
  </Link>
);

export const Catalog = ({ audience }) => (
  <div className="space-y-12">
    {[["Phones", ATT_CATALOG.phones], ["Tablets", ATT_CATALOG.tablets], ["Watches", ATT_CATALOG.watches]].map(([label, items]) => (
      <div key={label} data-testid={`att-catalog-${label.toLowerCase()}-${audience}`}>
        <h3 className="font-serif-display text-2xl md:text-3xl font-bold text-[#1d2329]">{label}</h3>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {items.map((d) => (
            <Reveal key={d.id}><DeviceCard d={d} /></Reveal>
          ))}
        </div>
      </div>
    ))}
    <p className="font-body text-[11px] leading-relaxed text-[#8a94a0]">
      All monthly device pricing requires 0% APR, 36-month installment agreement. $0 down for well-qualified customers.
      Tax on full price due at sale. Trade-in values and promo eligibility confirmed by AT&amp;T at time of order. Restrictions apply.
    </p>
  </div>
);

export const PlanCards = ({ audience }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid={`att-plans-${audience}`}>
    {ATT_PLAN_DETAILS[audience].map((p) => (
      <Reveal key={p.name}>
        <div className="h-full rounded-2xl border border-black/10 bg-white p-6 shadow-sm flex flex-col">
          <h4 className="font-serif-display text-lg font-bold text-[#1d2329]">{p.name}</h4>
          <p className="mt-2 font-body text-2xl font-bold" style={{ color: ATT_BLUE }}>{p.price}</p>
          <p className="font-body text-xs text-[#6b7683]">{p.per}</p>
          <ul className="mt-4 space-y-2">
            {p.features.map((f) => (
              <li key={f} className="flex items-start gap-2 font-body text-sm text-[#3c4650]">
                <Check size={14} className="mt-1 shrink-0" style={{ color: ATT_BLUE }} /> {f}
              </li>
            ))}
          </ul>
        </div>
      </Reveal>
    ))}
  </div>
);

export const InternetCards = ({ audience }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid={`att-internet-${audience}`}>
    {ATT_INTERNET[audience].map((p) => (
      <Reveal key={p.name}>
        <div className="h-full rounded-2xl border border-black/10 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between gap-2">
            <h4 className="font-serif-display text-lg font-bold text-[#1d2329]">{p.name}</h4>
            <span className="rounded-full px-3 py-1 font-body text-[11px] font-bold text-white whitespace-nowrap" style={{ backgroundColor: ATT_BLUE }}>{p.speed}</span>
          </div>
          <p className="mt-2 font-body text-2xl font-bold text-[#1d2329]">{p.price}</p>
          <p className="mt-2 font-body font-light text-sm text-[#3c4650] leading-relaxed">{p.desc}</p>
        </div>
      </Reveal>
    ))}
  </div>
);

export const PromoGrid = ({ audience }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid={`att-promos-${audience}`}>
    {ATT_PROMOS[audience].map((p, i) => (
      <Reveal key={p.title} delay={i * 0.03}>
        <div className="h-full rounded-2xl border border-black/10 bg-white p-6 shadow-sm">
          <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 font-body text-[11px] font-bold uppercase tracking-widest text-white" style={{ backgroundColor: ATT_BLUE }}>
            <Tag size={11} /> Promo
          </span>
          <h4 className="mt-4 font-serif-display text-xl font-bold text-[#1d2329]">{p.title}</h4>
          <p className="mt-2 font-body font-light text-sm text-[#3c4650] leading-relaxed">{p.desc}</p>
        </div>
      </Reveal>
    ))}
  </div>
);

const HEROES = ["Teachers", "Military", "Police Officers", "Doctors", "Nurses"];

export const HeroesBanner = () => (
  <div className="relative overflow-hidden" style={{ backgroundColor: ATT_BLUE }} data-testid="att-heroes-banner">
    <div className="flex items-stretch">
      <div className="relative z-10 shrink-0" data-testid="att-heroes-image">
        <img
          src="/att/service-heroes.jpg"
          alt="Teachers, police officers, doctors, nurses, and military members"
          className="h-32 md:h-44 w-auto object-cover"
        />
      </div>
      <div className="relative flex-1 overflow-hidden flex items-center">
        <div className="marquee-track">
          {[0, 1].map((dup) => (
            <span key={dup} className="inline-flex items-center">
              {HEROES.map((w) => (
                <span key={`${dup}-${w}`} className="inline-flex items-center whitespace-nowrap">
                  <span className="mx-4 font-serif-display text-xl md:text-3xl font-bold text-white">{w}</span>
                  <span className="font-body text-sm md:text-lg font-semibold text-[#fcf6ba]">get 20% OFF each line</span>
                  <span className="mx-4 text-white/70 font-body text-lg">★</span>
                </span>
              ))}
              <span className="inline-flex items-center whitespace-nowrap">
                <span className="mx-4 rounded-full border border-white/50 px-4 py-1 font-body text-[11px] md:text-xs font-bold uppercase tracking-[0.25em] text-white">
                  Verification required
                </span>
                <span className="mx-4 text-white/70 font-body text-lg">★</span>
              </span>
            </span>
          ))}
        </div>
      </div>
    </div>
  </div>
);
